import type { TelemetryDataPoint } from "@/types";

export interface TimeWindow {
  startTime: number;
  endTime: number;
}

/** Viewports covering at least this fraction of the full domain are served by the base fetch alone. */
export const DETAIL_FULL_VIEW_FRACTION = 0.95;

/**
 * Decide which high-res window to render for the current viewport.
 * - viewport at (>=95%) full view -> null: the base fetch already serves
 *   the chart and no detail fetch is needed (checked first, so even a
 *   covering active window releases)
 * - viewport covered by the active window -> keep it (cache-friendly pans)
 * - viewport swallowed the active window (zoomed back out) -> a fresh
 *   viewport window
 * - stale active window -> window spanning the uncovered part of the viewport
 *   (3x the uncovered span, unioned with the viewport, clamped to the domain);
 *   the union guarantees the result always covers the viewport
 * - no active window -> 3x-viewport window clamped to the domain
 */
export function resolveDetailWindow(
  viewport: TimeWindow,
  fullDomain: TimeWindow,
  activeWindow: TimeWindow | null,
): TimeWindow | null {
  const fullDuration = fullDomain.endTime - fullDomain.startTime;
  const viewDuration = viewport.endTime - viewport.startTime;
  if (fullDuration <= 0 || viewDuration <= 0) return null;

  const fullView = viewDuration / fullDuration >= DETAIL_FULL_VIEW_FRACTION;
  // A >=95% full view is always served by the base fetch alone — checked
  // first so even a covering active window (e.g. a full-domain window left
  // over from a padded zoom) releases instead of pinning the detail query.
  if (fullView) return null;

  const buildWindow = (spanStart: number, spanEnd: number): TimeWindow => {
    const pad = spanEnd - spanStart;
    return {
      startTime: Math.max(
        fullDomain.startTime,
        Math.min(spanStart - pad, viewport.startTime),
      ),
      endTime: Math.min(
        fullDomain.endTime,
        Math.max(spanEnd + pad, viewport.endTime),
      ),
    };
  };

  if (
    activeWindow &&
    viewport.startTime >= activeWindow.startTime &&
    viewport.endTime <= activeWindow.endTime
  ) {
    return activeWindow;
  }

  if (
    activeWindow &&
    viewport.startTime <= activeWindow.startTime &&
    viewport.endTime >= activeWindow.endTime
  ) {
    return buildWindow(viewport.startTime, viewport.endTime);
  }

  if (!activeWindow) {
    return buildWindow(viewport.startTime, viewport.endTime);
  }

  // The active window no longer covers the viewport but is not fully
  // zoomed past either: re-fetch around the uncovered span plus padding,
  // unioned with the viewport. The union guarantees the returned window
  // always covers the viewport, so no base-res hole survives a tick.
  const leftHole = activeWindow.startTime - viewport.startTime;
  const rightHole = viewport.endTime - activeWindow.endTime;
  const spanStart =
    leftHole > 0
      ? viewport.startTime
      : Math.max(viewport.startTime, activeWindow.endTime);
  const spanEnd =
    rightHole > 0
      ? viewport.endTime
      : Math.min(viewport.endTime, activeWindow.startTime);
  return buildWindow(spanStart, spanEnd);
}

/**
 * Merge a high-res detail fetch into the base series: base rows outside the
 * window are kept, base rows inside are replaced by the detail rows. Input
 * arrays are timestamp-sorted; output is timestamp-sorted.
 */
export function mergeTelemetryData(
  base: TelemetryDataPoint[],
  detail: TelemetryDataPoint[],
  detailWindow: TimeWindow,
): TelemetryDataPoint[] {
  if (detail.length === 0) return [...base];

  const outside = (p: TelemetryDataPoint) =>
    p.timestamp < detailWindow.startTime || p.timestamp > detailWindow.endTime;

  const merged: TelemetryDataPoint[] = [];
  let i = 0;
  let j = 0;
  while (i < base.length && j < detail.length) {
    const b = base[i]!;
    const d = detail[j]!;
    if (outside(b)) {
      merged.push(b);
      i++;
    } else if (b.timestamp < d.timestamp) {
      i++; // inside the window: detail wins over decimated base rows
    } else if (b.timestamp === d.timestamp) {
      merged.push(d);
      i++;
      j++;
    } else {
      merged.push(d);
      j++;
    }
  }
  while (i < base.length) {
    const b = base[i++]!;
    if (outside(b)) merged.push(b);
  }
  while (j < detail.length) merged.push(detail[j++]!);
  return merged;
}
